import React, { useState } from "react";
import { CheckIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  isEditing: boolean;
  name: string;
  onNameChange: (v: string) => void;
  options: string[];
  onOptionsChange: (v: string[]) => void;
  onSave: () => void;
  onClose: () => void;
}

const SpecialtyModal: React.FC<Props> = ({ isEditing, name, onNameChange, options, onOptionsChange, onSave, onClose }) => {
  const { t } = useLanguage();
  const [optionDraft, setOptionDraft] = useState("");

  const addOption = () => {
    const value = optionDraft.trim();
    if (!value || options.includes(value)) return;
    onOptionsChange([...options, value]);
    setOptionDraft("");
  };

  const removeOption = (value: string) => onOptionsChange(options.filter((o) => o !== value));

  return (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
      <div className="bg-gradient-to-r from-[#b2d12e]/10 to-[#a0c827]/10 border-b border-gray-200 p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[#b2d12e] uppercase mb-1">{t("admin.specialtyModal.eyebrow")}</p>
          <h2 className="text-2xl font-bold text-gray-900">{isEditing ? t("admin.specialtyModal.editTitle") : t("admin.specialtyModal.createTitle")}</h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      <div className="p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.specialtyModal.nameLabel")}</label>
        <input type="text" value={name} onChange={(e) => onNameChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSave()}
          placeholder={t("admin.specialtyModal.namePlaceholder")}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b2d12e] focus:border-transparent" />

        <label className="block text-sm font-semibold text-gray-700 mt-5 mb-2">
          {t("admin.specialtyModal.subOptionsLabel")} <span className="font-normal text-gray-400">{t("admin.specialtyModal.subOptionsHint")}</span>
        </label>
        <div className="flex gap-2 mb-3">
          <input type="text" value={optionDraft} onChange={(e) => setOptionDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
            placeholder={t("admin.specialtyModal.optionPlaceholder")}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b2d12e] focus:border-transparent" />
          <button type="button" onClick={addOption}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
        {options.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {options.map((o) => (
              <span key={o} className="inline-flex items-center gap-1 bg-[#b2d12e]/15 text-gray-800 text-xs font-medium px-3 py-1 rounded-full">
                {o}
                <button type="button" onClick={() => removeOption(o)}
                  className="text-gray-400 hover:text-red-500 transition ml-1" aria-label={t("admin.specialtyModal.removeOption", { option: o })}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
        <button onClick={onClose}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors">
          {t("common.cancel")}
        </button>
        <button onClick={onSave}
          className="px-6 py-2 bg-gradient-to-r from-[#b2d12e] to-[#a0c827] text-gray-900 rounded-lg font-semibold hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-2">
          <CheckIcon className="w-5 h-5" /> {t("common.save")}
        </button>
      </div>
    </div>
  </div>
  );
};

export default SpecialtyModal;
